import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  AddOrderDesignCommentDocument,
  GenerateOrderDesignCommentAttachmentUploadLinkDocument,
  GetEstimatedDeliveryDateDocument,
  GetInProgressOrderDesignDocument,
  GetOrderDeliveryInfoDocument,
  GetOrderDesignByIdDocument,
  GetOrderDesignFilesDocument,
  GetOrderDesignsByOrderIdDocument,
  ReviewOrderDesignDocument,
} from "../../gql/generated/graphql";
import { useCallback, useMemo } from "react";
import {
  getRemainingTimeToReview,
  type OrderDesign,
} from "@jasper/client-models";
import { formatOrderDesign } from "./utils";

interface UseOrderDesignReviewProps {
  orderId: string;
}

interface UseOrderDesignReviewHookResult {
  openedOrderDesign: OrderDesign | null;
  remainingTimeToReview: number | null;
  isLoading: boolean;
  getOrderDesignFiles: () => Promise<string[]>;
  error?: Error;
  rejectOrderDesign: (params: {
    reason: string;
    files: File[];
  }) => Promise<void>;
  validateOrderDesign: () => Promise<void>;
  requestedOrderDesign: OrderDesign | null;
  getNewDeliveryDateAfterRejection: () => Promise<{
    newDeliveryDate: Date;
    initialDeliveryDate: Date;
  }>;
}

export const useOrderDesignReview = ({
  orderId,
}: UseOrderDesignReviewProps): UseOrderDesignReviewHookResult => {
  const { data, loading, error } = useQuery(GetInProgressOrderDesignDocument, {
    variables: { orderId },
  });

  const [getOrderDeliveryInfoQuery] = useLazyQuery(
    GetOrderDeliveryInfoDocument
  );
  const [getEstimatedDeliveryDateQuery] = useLazyQuery(
    GetEstimatedDeliveryDateDocument
  );
  const [getOrderDesignFilesQuery] = useLazyQuery(GetOrderDesignFilesDocument);
  const [reviewOrderDesignMutation] = useMutation(ReviewOrderDesignDocument);
  const [addOrderDesignComment] = useMutation(AddOrderDesignCommentDocument);
  const [generateOrderDesignCommentAttachmentUploadLink] = useMutation(
    GenerateOrderDesignCommentAttachmentUploadLinkDocument
  );

  const openedOrderDesign = useMemo((): OrderDesign | null => {
    const orderDesign = data?.getInProgressOrderDesign;

    if (orderDesign?.status !== "OPENED") {
      return null;
    }

    return formatOrderDesign(orderDesign);
  }, [data]);

  const requestedOrderDesign = useMemo((): OrderDesign | null => {
    const orderDesign = data?.getInProgressOrderDesign;

    if (orderDesign?.status !== "REQUESTED") {
      return null;
    }

    return formatOrderDesign(orderDesign);
  }, [data]);

  const remainingTimeToReview = useMemo(() => {
    if (!openedOrderDesign) {
      return null;
    }

    return getRemainingTimeToReview({ orderDesign: openedOrderDesign });
  }, [openedOrderDesign]);

  const getOrderDesignFiles = useCallback(async () => {
    if (!openedOrderDesign?.id) {
      return [];
    }

    const { data, error } = await getOrderDesignFilesQuery({
      variables: { orderDesignId: openedOrderDesign.id },
    });

    if (error) {
      throw error;
    }

    return data?.getOrderDesignFiles ?? [];
  }, [openedOrderDesign, getOrderDesignFilesQuery]);

  const reviewOrderDesign = useCallback(
    async (review: "VALIDATED" | "REJECTED") => {
      if (!openedOrderDesign?.id) {
        throw new Error("No opened order design to review");
      }

      try {
        await reviewOrderDesignMutation({
          variables: {
            orderDesignId: openedOrderDesign.id,
            review,
          },
          refetchQueries: [
            { query: GetInProgressOrderDesignDocument, variables: { orderId } },
            { query: GetOrderDesignsByOrderIdDocument, variables: { orderId } },
            {
              query: GetOrderDesignByIdDocument,
              variables: { orderDesignId: openedOrderDesign.id },
            },
          ],
          awaitRefetchQueries: true,
        });
      } catch (error) {
        console.error("Failed to review order design", error);
        throw error;
      }
    },
    [openedOrderDesign, reviewOrderDesignMutation, orderId]
  );

  const rejectOrderDesign = useCallback(
    async ({ reason, files = [] }: { reason: string; files: File[] }) => {
      if (!openedOrderDesign?.id) {
        throw new Error("No opened order design to reject");
      }

      if (!reason.trim()) {
        throw new Error("Rejection reason is required");
      }

      const res = await addOrderDesignComment({
        variables: {
          orderDesignId: openedOrderDesign.id,
          comment: reason,
        },
      });

      if (!res.data?.addOrderDesignComment.id) {
        throw new Error("Failed to add rejection comment");
      }

      const commentId = res.data.addOrderDesignComment.id;
      if (files.length) {
        const results = await Promise.allSettled(
          files.map(file =>
            generateOrderDesignCommentAttachmentUploadLink({
              variables: {
                orderDesignId: openedOrderDesign.id,
                commentId,
                filename: file.name,
              },
            })
          )
        );

        const uploadResults = await Promise.allSettled(
          results.map(async (result, i) => {
            if (
              result.status === "rejected" ||
              !result.value?.data
                ?.generateOrderDesignCommentAttachmentUploadLink
            ) {
              throw new Error(
                `Failed to generate upload link for ${files[i].name}`
              );
            }

            const { url, fields } =
              result.value.data.generateOrderDesignCommentAttachmentUploadLink;
            const formData = new FormData();
            Object.entries(fields).forEach(([key, value]) => {
              formData.append(key, value as string);
            });

            formData.append("file", files[i]);

            const res = await fetch(url, {
              method: "POST",
              body: formData,
            });

            if (!res.ok) {
              throw new Error(`Failed to upload file ${files[i].name}`);
            }
          })
        );

        if (uploadResults.some(result => result.status === "rejected")) {
          throw new Error("Failed to upload one or more rejection files");
        }
      }

      await reviewOrderDesign("REJECTED");
    },
    [
      openedOrderDesign,
      addOrderDesignComment,
      generateOrderDesignCommentAttachmentUploadLink,
      reviewOrderDesign,
    ]
  );

  const getNewDeliveryDateAfterRejection = useCallback(async () => {
    if (!openedOrderDesign?.id) {
      throw new Error("No opened order design to get estimated delivery date");
    }

    const orderDeliveryInfoRes = await getOrderDeliveryInfoQuery({
      variables: { orderId: openedOrderDesign.orderId },
    });

    if (orderDeliveryInfoRes.error) {
      throw orderDeliveryInfoRes.error;
    }

    if (!orderDeliveryInfoRes.data?.getOrderById) {
      throw new Error("Failed to get order delivery info");
    }

    const estimatedDeliveryDateRes = await getEstimatedDeliveryDateQuery({
      variables: { orderId: openedOrderDesign.orderId },
    });

    if (estimatedDeliveryDateRes.error) {
      throw estimatedDeliveryDateRes.error;
    }

    if (!estimatedDeliveryDateRes.data?.getEstimatedDeliveryDate) {
      throw new Error("Failed to get estimated delivery date");
    }

    const { getEstimatedDeliveryDate } = estimatedDeliveryDateRes.data;

    return {
      newDeliveryDate: new Date(
        orderDeliveryInfoRes.data.getOrderById.isRush
          ? getEstimatedDeliveryDate.estimatedRushDeliveryDate
          : getEstimatedDeliveryDate.estimatedOptimalDeliveryDate
      ),
      initialDeliveryDate: new Date(
        orderDeliveryInfoRes.data.getOrderById.deliveryDate
      ),
    };
  }, [
    openedOrderDesign,
    getEstimatedDeliveryDateQuery,
    getOrderDeliveryInfoQuery,
  ]);

  return {
    validateOrderDesign: () => reviewOrderDesign("VALIDATED"),
    rejectOrderDesign,
    getOrderDesignFiles,
    openedOrderDesign,
    requestedOrderDesign,
    remainingTimeToReview,
    getNewDeliveryDateAfterRejection,
    isLoading: loading,
    error,
  };
};
